import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { IdentificationBadge, Student, UserCircle, Briefcase, Plus, Printer, ArrowsClockwise, UploadSimple, FileText, X, Receipt, WarningCircle, BookOpen, Question, Trash, CaretDown, MapPin, MagnifyingGlass, Bus, Path } from "@phosphor-icons/react";
import { printReceipt } from "@/lib/printReceipt";
import { PassRenewModal } from "@/components/RenewTopupModals";
import { PassbookModal, ReportLostModal, InfoModal, PASS_TERMS, PASS_FAQ } from "@/components/PassbookAndInfo";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const cryptoId = () => Math.random().toString(36).slice(2, 10);
const BUS_SERVICES = ["SHUTTLE", "SHUTTLE EV", "LOCAL", "LOCAL EV", "SHUTTLE SPECIAL"];
const GOA_DISTRICTS = ["North Goa", "South Goa"];
const GOA_TALUKAS = ["Pernem","Bardez","Bicholim","Tiswadi","Ponda","Sattari","Sanguem","Quepem","Salcete","Mormugao","Canacona","Dharbandora"];
const BLOOD_GROUPS = ["A+","A-","B+","B-","AB+","AB-","O+","O-","Unknown"];
const COLLECTION_COUNTERS = ["Margao", "Panaji", "Vasco", "Mapusa"];
const EMPLOYEE_STATUS = ["Active", "On Probation", "On Leave", "Suspended", "Retired"];

const REQUIRES_EXTENDED = (cat) => /Private Application|Private Business|Industry Worker|Freedom Fighter|Person with Disability|KTCL Staff|Senior Citizen|Student|Govt\. Employee/i.test(cat);
const REQUIRES_RESIDENCY = (cat) => /^(Private Application|Private Business|Industry Worker)$/i.test(cat);

// Category-specific concession proof.
//   single doc: { field, label }
//   either-or:  { altOf: [ {field,label}, {field,label} ], note }
const CONCESSION_DOC = {
  "Senior Citizen":         { field: "social_welfare_url",   label: "Social Welfare Certificate *" },
  "Freedom Fighter":        { field: "social_welfare_url",   label: "Social Welfare Certificate *" },
  "Person with Disability": { altOf: [
                                { field: "social_welfare_url", label: "Social Welfare Certificate" },
                                { field: "udid_card_url",      label: "UDID Card (Swavlamban Card)" },
                              ],
                              note: "Note: Upload either Social Welfare Certificate OR UDID Card (Swavlamban Card)." },
  "Student":                { field: "bonafide_url",         label: "Bonafide Certificate from Institution *" },
  "Govt. Employee":         { field: "govt_id_url",          label: "Government Employee ID Card *" },
  "KTCL Staff":             { field: "ktcl_staff_url",       label: "KTCL ID Card *" },
};
const lookupFare = (fares, cat, residency, pass_type) => {
  const entry = fares?.[cat];
  if (!entry) return null;
  // residency-aware nested fare table
  if (typeof entry === "object" && !Array.isArray(entry) && entry[residency]) return entry[residency]?.[pass_type] ?? null;
  // flat fare table
  if (typeof entry === "object" && entry[pass_type] !== undefined) return entry[pass_type];
  return null;
};

export default function PassManagement() {
  const [opts, setOpts] = useState({ categories: [], types: [], fares: {} });
  const [passes, setPasses] = useState([]);
  const [stops, setStops] = useState([]);
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(null);
  const blank = {
    category:"General", pass_type:"Monthly",
    holder_name:"", first_name:"", middle_name:"", last_name:"",
    mobile:"", aadhar_no:"", email:"", date_of_birth:"", age:"", gender:"Male", blood_group:"Unknown",
    dept_name:"",
    employee_code:"", employee_status:"Active", joining_date:"",
    company_address:"", locality:"", city:"", district:"North Goa", taluka:"Tiswadi", state:"Goa", pincode:"",
    residency:"Resident of GOA",
    valid_from:new Date().toISOString().slice(0,10), valid_to:"", amount:1260, depot:"Panaji",
    bus_service:"LOCAL", bus_services:["SHUTTLE"], counter_stop:"", from_stop:"", to_stop:"",
    routes:[{ id: cryptoId(), from_stop: "", to_stop: "", via_stop: "", pass_type: "Monthly",
              distance_km: 0, single_fare: 0, amount: 0, fare_source: "" }],
    photo_url:"", aadhar_url:"", bonafide_url:"", police_verification_url:"",
    social_welfare_url:"", udid_card_url:"", govt_id_url:"", ktcl_staff_url:"", imc_url:"",
    consent_signed:false,
  };
  const [f, setF] = useState(blank);
  const [pickerFor, setPickerFor] = useState(null); // {idx, target, placeholder}
  const [renewPass, setRenewPass] = useState(null);
  const [passbookPass, setPassbookPass] = useState(null);
  const [lostPass, setLostPass] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);

  const load = async () => {
    const [o, p] = await Promise.all([api.get("/passes/options"), api.get("/passes")]);
    setOpts(o.data); setPasses(p.data);
  };
  useEffect(() => { load(); api.get("/bus-stops/all").then(({data})=>setStops(data.slice(0, 500))); }, []);

  useEffect(() => {
    const days = { Daily:1, Weekly:7, Monthly:30, Quarterly:90, "Half Yearly":180, Yearly:365 }[f.pass_type] || 30;
    const to = new Date(f.valid_from); to.setDate(to.getDate() + days);
    const minAmt = opts.min_amount ?? 500;
    const maxAmt = opts.max_amount ?? 20000;
    setF((x) => {
      // If any route row has stops chosen, prefer the per-route sum and leave amount untouched here.
      const hasRoutes = (x.routes || []).some((r) => r.from_stop && r.to_stop);
      const raw = lookupFare(opts.fares, x.category, x.residency, x.pass_type) ?? x.amount;
      const clamped = Math.min(maxAmt, Math.max(minAmt, raw));
      return { ...x,
        amount: hasRoutes ? x.amount : clamped,
        valid_to: to.toISOString().slice(0,10),
        holder_name: [x.first_name, x.middle_name, x.last_name].filter(Boolean).join(" ") || x.holder_name };
    });
  }, [f.category, f.pass_type, f.residency, f.valid_from, opts.fares, opts.min_amount, opts.max_amount, f.first_name, f.middle_name, f.last_name]);

  // Per-route fare estimation — recompute whenever any from/to/pass_type/category changes.
  // Uses the admin-side endpoint /api/passes/estimate-route (mirrors customer logic).
  const routesKey = (f.routes || []).map((r) => `${r.from_stop}|${r.to_stop}|${r.via_stop}|${r.pass_type}`).join("·");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const updated = await Promise.all((f.routes || []).map(async (r) => {
        if (!r.from_stop || !r.to_stop) {
          return { ...r, amount: 0, distance_km: 0, single_fare: 0, fare_source: "" };
        }
        try {
          const { data } = await api.post("/passes/estimate-route", {
            from_stop: r.from_stop, to_stop: r.to_stop,
            via_stop: r.via_stop || "",
            pass_type: r.pass_type || f.pass_type,
            category: f.category,
          });
          return { ...r,
            amount: data.amount, distance_km: data.distance_km,
            single_fare: data.single_fare, fare_source: data.fare_source };
        } catch { return { ...r, amount: 0 }; }
      }));
      if (cancelled) return;
      const total = Math.round(updated.reduce((s, r) => s + (Number(r.amount) || 0), 0));
      setF((x) => ({ ...x, routes: updated, amount: total > 0 ? total : x.amount }));
    })();
    return () => { cancelled = true; };
  }, [routesKey, f.category]);

  const uploadFile = async (slot, file) => {
    if (!file) return;
    if (file.size > 5*1024*1024) { toast.error("File > 5MB"); return; }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!["jpg","jpeg","png","pdf"].includes(ext)) { toast.error("Only JPG/PNG/PDF"); return; }
    setUploading(slot);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      setF(x=>({...x, [slot]: data.url}));
      toast.success(`Uploaded ${file.name}`);
    } catch { toast.error("Upload failed"); }
    setUploading(null);
  };

  const requireExtended = REQUIRES_EXTENDED(f.category);
  const requireResidency = REQUIRES_RESIDENCY(f.category);
  const concessionDoc = CONCESSION_DOC[f.category] || null;

  // ----- Multi-route helpers (mirror MajheApp PassHub) -----
  const addRoute = () => {
    if ((f.routes || []).length >= 5) { toast.warning("Maximum 5 routes per pass"); return; }
    setF((x) => ({ ...x, routes: [...(x.routes || []), {
      id: cryptoId(), from_stop: "", to_stop: "", via_stop: "", pass_type: x.pass_type,
      distance_km: 0, single_fare: 0, amount: 0, fare_source: "",
    }]}));
  };
  const removeRoute = (idx) => {
    if ((f.routes || []).length === 1) { toast.warning("At least one route is required"); return; }
    setF((x) => ({ ...x, routes: x.routes.filter((_, i) => i !== idx) }));
  };
  const updateRoute = (idx, patch) => setF((x) => ({
    ...x, routes: x.routes.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));
  const toggleBusService = (svc) => setF((x) => ({
    ...x, bus_services: (x.bus_services || []).includes(svc)
      ? x.bus_services.filter((s) => s !== svc)
      : [...(x.bus_services || []), svc],
  }));

  const issue = async () => {
    if (!f.holder_name || !f.mobile) { toast.error("Name & mobile required"); return; }
    if (requireExtended && !f.consent_signed) { toast.error("Tick the registration consent"); return; }
    if ((f.bus_services || []).length === 0) { toast.error("Select at least one Bus Service"); return; }
    const populatedRoutes = (f.routes || []).filter((r) => r.from_stop && r.to_stop);
    if (populatedRoutes.length === 0) { toast.error("Add at least one Pass Route (From → To)"); return; }
    const minAmt = opts.min_amount ?? 500;
    const maxAmt = opts.max_amount ?? 20000;
    if (+f.amount < minAmt) { toast.error(`Pass amount must be at least ₹${minAmt}`); return; }
    if (+f.amount > maxAmt) { toast.error(`Pass amount cannot exceed ₹${maxAmt}`); return; }
    if (requireExtended && !f.aadhar_url) { toast.error("Aadhar Card upload required"); return; }
    if (requireExtended && !f.photo_url) { toast.error("Passport-size photo required"); return; }
    if (f.category === "Govt. Employee" && !f.dept_name) { toast.error("Department Name is required"); return; }
    if (f.category === "KTCL Staff" && (!f.employee_code || !f.joining_date)) { toast.error("Employee Code and Joining Date are required"); return; }
    if (concessionDoc?.field && !f[concessionDoc.field]) { toast.error(`${concessionDoc.label.replace(/ \*$/,"")} upload required`); return; }
    if (concessionDoc?.altOf && !concessionDoc.altOf.some(d => f[d.field])) {
      toast.error(`Upload either ${concessionDoc.altOf.map(d=>d.label).join(" or ")}`); return;
    }
    if (requireResidency && f.residency === "Working in GOA from Other State" && !f.bonafide_url && !f.police_verification_url) {
      toast.error("Upload Company Bonafide OR Police Verification certificate"); return;
    }
    try {
      const payload = {
        ...f, age: f.age ? +f.age : null,
        routes: populatedRoutes.map((r) => ({
          from_stop: r.from_stop, to_stop: r.to_stop,
          via_stop: r.via_stop || "", pass_type: r.pass_type || f.pass_type,
        })),
        bus_services: f.bus_services || [],
      };
      const { data } = await api.post("/passes/issue", payload);
      setPasses((p) => [data, ...p]);
      toast.success(`Pass ${data.pass_no} issued · ₹${data.amount} · ${populatedRoutes.length} route${populatedRoutes.length>1?"s":""}`);
      printReceipt({ kind: "pass", data });
      setShow(false); setStep(1); setF(blank);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to issue pass"); }
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Passenger Passes</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Pass Management</h1>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          <button onClick={()=>{
            const csv = "category,pass_type,holder_name,mobile,valid_from,valid_to,amount,depot\nGeneral,Monthly,John Doe,9999988888,2026-02-15,2026-03-17,1260,Panaji";
            const url = URL.createObjectURL(new Blob([csv], {type:"text/csv"}));
            const a = document.createElement("a"); a.href = url; a.download = "pass-bulk-template.csv"; a.click();
          }} className="px-3 py-2 border border-slate-200 rounded-md text-xs hover:bg-slate-50 whitespace-nowrap"><span className="hidden sm:inline">CSV </span>Template</button>
          <button data-testid="pass-tc-btn" onClick={()=>setShowTerms(true)} className="px-3 py-2 border border-slate-200 rounded-md text-xs hover:bg-slate-50 inline-flex items-center gap-1 whitespace-nowrap"><BookOpen size={12}/>T&amp;C</button>
          <button data-testid="pass-faq-btn" onClick={()=>setShowFAQ(true)} className="px-3 py-2 border border-slate-200 rounded-md text-xs hover:bg-slate-50 inline-flex items-center gap-1 whitespace-nowrap"><Question size={12}/>FAQ</button>
          <button onClick={()=>{ setF(blank); setStep(1); setShow(true); }} className="ktcl-btn-primary px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap">
            <Plus size={14}/><span className="hidden sm:inline">Issue New Pass</span><span className="sm:hidden">New Pass</span>
          </button>
          <label className="px-3 sm:px-4 py-2 border border-slate-200 rounded-md text-xs sm:text-sm font-semibold flex items-center gap-1.5 cursor-pointer hover:bg-slate-50 whitespace-nowrap">
            <UploadSimple size={14}/><span className="hidden sm:inline">Bulk CSV</span><span className="sm:hidden">Bulk</span>
            <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
              const file = e.target.files[0]; if (!file) return;
              const text = await file.text();
              const [header, ...rest] = text.trim().split(/\r?\n/);
              const cols = header.split(",").map(c => c.trim());
              const rows = rest.map(r => { const v = r.split(","); return Object.fromEntries(cols.map((c,i)=>[c, (v[i]||"").trim()])); });
              try { const { data } = await api.post("/passes/bulk", { rows });
                toast.success(`Bulk: ${data.created} passes created`); load();
              } catch { toast.error("Bulk upload failed"); }
              e.target.value = "";
            }}/>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {opts.categories.map((c) => {
          const count = passes.filter(p => p.category === c).length;
          return (
            <button key={c} onClick={() => { setF({...blank, category: c}); setStep(1); setShow(true); }}
                    className="ktcl-card ktcl-card-hover p-4 text-left transition-all hover:border-[#1E3A8A]">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold text-slate-800 leading-snug">{c}</div>
                <div className="font-display text-2xl font-bold text-[#1E3A8A] tabular-nums shrink-0">{count}</div>
              </div>
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">Issue new <span aria-hidden>→</span></div>
            </button>
          );
        })}
      </div>

      <div className="ktcl-card overflow-x-auto scroll-thin">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-display font-bold text-lg">Recent Issued Passes</h3>
        </div>
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50"><tr className="text-left text-xs text-slate-500">
            {"Pass No,Holder,Category,Type,Valid From,Valid To,Amount,Status,Action".split(",").map(h => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {passes.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-500">No passes issued yet — click &ldquo;Issue New Pass&rdquo;</td></tr>}
            {passes.map(p => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-mono font-bold text-[#1E3A8A]">{p.pass_no}</td>
                <td className="px-3 py-2">{p.holder_name}</td>
                <td className="px-3 py-2 text-xs"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px]">{p.category}</span></td>
                <td className="px-3 py-2">{p.pass_type}</td>
                <td className="px-3 py-2 text-xs">{p.valid_from}</td>
                <td className="px-3 py-2 text-xs">{p.valid_to}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">₹{p.amount}</td>
                <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">{p.status}</span></td>
                <td className="px-3 py-2 text-right space-x-1">
                  <button onClick={()=>printReceipt({kind:"pass", data:p})} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1"><Printer size={11}/>Reprint</button>
                  <button data-testid={`pass-passbook-${p.pass_no}`} onClick={()=>setPassbookPass(p)} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1"><Receipt size={11}/>Passbook</button>
                  <button data-testid={`pass-renew-${p.pass_no}`} onClick={()=>setRenewPass(p)} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1"><ArrowsClockwise size={11}/>Renew</button>
                  {p.status !== "Lost" && p.status !== "Cancelled" && (
                    <button data-testid={`pass-lost-${p.pass_no}`} onClick={()=>setLostPass(p)} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1"><WarningCircle size={11}/>Lost</button>
                  )}
                  <button onClick={async ()=>{
                    if (!window.confirm(`Cancel pass ${p.pass_no} and refund ₹${p.amount}?`)) return;
                    try { const { data } = await api.post(`/passes/${p.id}/cancel`);
                      toast.success(`Cancelled · refund ₹${data.refund}`); load();
                    } catch { toast.error("Cancel failed"); }
                  }} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50">Cancel</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {renewPass && <PassRenewModal pass={renewPass} onClose={()=>setRenewPass(null)} onDone={load}/>}
      {passbookPass && <PassbookModal kind="pass" item={passbookPass} onClose={()=>setPassbookPass(null)}/>}
      {lostPass && <ReportLostModal kind="pass" item={lostPass} onClose={()=>setLostPass(null)} onDone={load}/>}
      {showTerms && <InfoModal title="Smart Transit Pass · Terms & Conditions" kind="pass-terms" fallbackSections={PASS_TERMS} onClose={()=>setShowTerms(false)} icon={<BookOpen size={18}/>}/>}
      {showFAQ && <InfoModal title="Smart Transit Pass · FAQ" kind="pass-faq" fallbackSections={PASS_FAQ} onClose={()=>setShowFAQ(false)} icon={<Question size={18}/>}/>}
      {show && (
        <div className="fixed inset-0 bg-black/50 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShow(false)}>
          <div className="bg-white w-full max-w-4xl max-h-[90dvh] sm:max-h-[92vh] overflow-auto rounded-t-2xl sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white p-4 sm:p-5 border-b border-slate-200 flex items-center justify-between z-10 rounded-t-2xl sm:rounded-t-lg">
              <div>
                <h3 className="font-display text-xl font-bold">Issue New Pass · {f.category}</h3>
                <div className="text-xs text-slate-500">Step {step} of {requireExtended?4:3}</div>
              </div>
              <button onClick={() => setShow(false)} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
            </div>

            <div className="p-5 space-y-4">
              {step === 1 && (
                <>
                  <Section title="1. Passenger Category *">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {opts.categories.map(c => (
                        <button key={c} onClick={() => setF({...f, category:c})}
                                className={`px-3 py-3 border rounded-md text-sm leading-snug text-left transition-colors ${f.category===c?"border-[#1E3A8A] bg-blue-50 font-semibold text-[#1E3A8A]":"border-slate-200 hover:bg-slate-50 hover:border-slate-300"}`}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </Section>
                  {requireResidency && (
                    <Section title="Residency *">
                      <div className="flex gap-2">
                        {["Working in GOA from Other State","Resident of GOA"].map(r => (
                          <label key={r} className={`flex-1 p-3 border rounded-md cursor-pointer text-sm ${f.residency===r?"border-[#1E3A8A] bg-blue-50 font-semibold":"border-slate-200"}`}>
                            <input type="radio" checked={f.residency===r} onChange={()=>setF({...f, residency:r})} className="mr-2"/>{r}
                          </label>
                        ))}
                      </div>
                    </Section>
                  )}
                  <Section title="2. Pass Type *">
                    <div className="flex flex-wrap gap-2">
                      {opts.types.map(t => (
                        <button key={t} onClick={() => setF({...f, pass_type:t})}
                                className={`px-3 py-1.5 border rounded-md text-sm ${f.pass_type===t?"border-[#1E3A8A] bg-blue-50 font-semibold":"border-slate-200"}`}>
                          {t} Pass
                        </button>
                      ))}
                    </div>
                  </Section>
                </>
              )}

              {step === 2 && (
                <>
                  <Section title="Personal Details *">
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="First Name *"><Inp v={f.first_name} on={v=>setF({...f, first_name:v})}/></Field>
                      <Field label="Middle Name"><Inp v={f.middle_name} on={v=>setF({...f, middle_name:v})}/></Field>
                      <Field label="Last Name *"><Inp v={f.last_name} on={v=>setF({...f, last_name:v})}/></Field>
                      <Field label="Mobile No *"><Inp v={f.mobile} on={v=>setF({...f, mobile:v})}/></Field>
                      <Field label="Aadhar No *"><Inp v={f.aadhar_no} on={v=>setF({...f, aadhar_no:v})}/></Field>
                      <Field label="Email"><Inp v={f.email} on={v=>setF({...f, email:v})} placeholder="Enter Email id"/></Field>
                      <Field label="Date of Birth *"><Inp type="date" v={f.date_of_birth} on={v=>setF({...f, date_of_birth:v})}/></Field>
                      <Field label="Age *"><Inp type="number" v={f.age} on={v=>setF({...f, age:v})}/></Field>
                      <Field label="Gender">
                        <Sel v={f.gender} on={v=>setF({...f, gender:v})} options={["Male","Female","Other"]}/>
                      </Field>
                      <Field label="Blood Group *">
                        <Sel v={f.blood_group} on={v=>setF({...f, blood_group:v})} options={BLOOD_GROUPS}/>
                      </Field>
                    </div>
                  </Section>
                  {f.category === "Govt. Employee" && (
                    <Section title="DEPT / INST / SEMI-GOVT *">
                      <Inp v={f.dept_name} on={v=>setF({...f, dept_name:v})} placeholder="Department Name"/>
                    </Section>
                  )}
                  {f.category === "KTCL Staff" && (
                    <Section title="Employment Details *">
                      <div className="grid grid-cols-3 gap-3">
                        <Field label="Employee Code *"><Inp v={f.employee_code} on={v=>setF({...f, employee_code:v})} placeholder="e.g. EMP0123"/></Field>
                        <Field label="Employee Status *">
                          <Sel v={f.employee_status} on={v=>setF({...f, employee_status:v})} options={EMPLOYEE_STATUS}/>
                        </Field>
                        <Field label="Joining Date *"><Inp type="date" v={f.joining_date} on={v=>setF({...f, joining_date:v})}/></Field>
                      </div>
                    </Section>
                  )}
                  {requireExtended && (() => {
                    const isCompanyMode = requireResidency && f.residency === "Working in GOA from Other State";
                    const sectionTitle = !isCompanyMode ? "Residence Address *"
                      : f.category === "Industry Worker" ? "Workplace Address *"
                      : f.category === "Private Business" ? "Business Address *"
                      : "Company Address *";
                    const lineLabel = !isCompanyMode ? "Residence Address Line *" : "Address Line *";
                    const linePh = !isCompanyMode ? "Enter details for Inside Goa" : "Enter address details";
                    return (
                    <Section title={sectionTitle}>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label={lineLabel}><Inp v={f.company_address} on={v=>setF({...f, company_address:v})} placeholder={linePh}/></Field>
                        <Field label="Locality *"><Inp v={f.locality} on={v=>setF({...f, locality:v})} placeholder="Enter Area Name"/></Field>
                        <Field label="City / Village *"><Inp v={f.city} on={v=>setF({...f, city:v})} placeholder="Enter City Name"/></Field>
                        <Field label="District *">
                          <Sel v={f.district} on={v=>setF({...f, district:v})} options={GOA_DISTRICTS} placeholder="--- Select ---"/>
                        </Field>
                        <Field label="Taluka *">
                          <Sel v={f.taluka} on={v=>setF({...f, taluka:v})} options={GOA_TALUKAS} placeholder="--- Select ---"/>
                        </Field>
                        <Field label="State *"><Inp v={f.state} on={v=>setF({...f, state:v})}/></Field>
                        <Field label="Pincode *"><Inp v={f.pincode} on={v=>setF({...f, pincode:v})} placeholder="Enter 6-digit pincode"/></Field>
                      </div>
                    </Section>
                    );
                  })()}
                </>
              )}

              {step === 3 && requireExtended && (() => {
                const needsEmploymentProof = requireResidency && f.residency === "Working in GOA from Other State";
                return (
                <Section title="Upload Files">
                  <div className="text-[11px] text-slate-500 mb-2">Note: File should not be more than 5MB, and the file type should be JPG, JPEG, PNG, or PDF only.</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Upload label="Passport-size Image *" url={f.photo_url} loading={uploading==="photo_url"} onUpload={(file)=>uploadFile("photo_url", file)}/>
                    <Upload label="Aadhar Card *" url={f.aadhar_url} loading={uploading==="aadhar_url"} onUpload={(file)=>uploadFile("aadhar_url", file)}/>
                    {concessionDoc?.field && (
                      <Upload label={concessionDoc.label} url={f[concessionDoc.field]} loading={uploading===concessionDoc.field} onUpload={(file)=>uploadFile(concessionDoc.field, file)}/>
                    )}
                    {concessionDoc?.altOf?.map(d => (
                      <Upload key={d.field} label={d.label} url={f[d.field]} loading={uploading===d.field} onUpload={(file)=>uploadFile(d.field, file)}/>
                    ))}
                    {needsEmploymentProof && (<>
                      <Upload label="Company Bonafide Certificate" url={f.bonafide_url} loading={uploading==="bonafide_url"} onUpload={(file)=>uploadFile("bonafide_url", file)}/>
                      <Upload label="Police Verification Certificate" url={f.police_verification_url} loading={uploading==="police_verification_url"} onUpload={(file)=>uploadFile("police_verification_url", file)}/>
                    </>)}
                  </div>
                  {concessionDoc?.note &&
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                      {concessionDoc.note}
                    </div>}
                  {needsEmploymentProof &&
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                      Note: Upload <b>either</b> Company Bonafide Certificate <b>OR</b> Police Verification Certificate to verify employment.
                    </div>}
                </Section>
                );
              })()}

              {((step === 3 && !requireExtended) || step === 4) && (
                <>
                  <Section title="Pass Details">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Please select Counter to collect your Pass: *">
                        <select data-testid="pass-collection-counter" value={f.counter_stop} onChange={e=>setF({...f, counter_stop:e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
                          <option value="">— Select counter —</option>
                          {COLLECTION_COUNTERS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field>
                      <Field label="Depot"><Inp v={f.depot} on={v=>setF({...f, depot:v})}/></Field>
                      <Field label="Valid From"><Inp type="date" v={f.valid_from} on={v=>setF({...f, valid_from:v})}/></Field>
                      <Field label="Valid To"><Inp v={f.valid_to} on={v=>setF({...f, valid_to:v})}/></Field>
                    </div>
                  </Section>

                  {/* STEP A — Bus Service multi-select (KTCL portal: SHUTTLE / SHUTTLE EV / LOCAL / LOCAL EV / SHUTTLE SPECIAL) */}
                  <Section title="Bus Service *">
                    <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1.5">
                      <Bus size={12} weight="bold"/> Pick the KTCL service classes this pass should be valid on.
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="pass-bus-service-pills">
                      {BUS_SERVICES.map((s) => {
                        const active = (f.bus_services || []).includes(s);
                        return (
                          <button key={s} type="button"
                            data-testid={`pass-bus-${s.replace(/\s+/g, "-")}`}
                            onClick={() => toggleBusService(s)}
                            className={`px-3 py-1.5 rounded-full text-[12px] font-bold border transition-all ${
                              active
                                ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                                : "bg-white text-[#1E3A8A] border-[#C7D2FE] hover:bg-[#EEF2FF]"
                            }`}>
                            {active ? "✓ " : ""}{s}
                          </button>
                        );
                      })}
                    </div>
                  </Section>

                  {/* STEP B — Pass Routes table (up to 5 routes per pass) */}
                  <Section title="Pass Routes *">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                        <Path size={12} weight="bold"/> Pick the route(s) for which this pass is valid. Amount auto-computed from GTFS fare.
                      </div>
                      <button type="button" onClick={addRoute} disabled={(f.routes || []).length >= 5}
                        data-testid="pass-add-route"
                        className="px-3 py-1.5 rounded-md text-[11.5px] font-bold bg-emerald-600 text-white shadow-sm disabled:opacity-40 inline-flex items-center gap-1">
                        <Plus size={12} weight="bold"/> Add Row
                      </button>
                    </div>
                    <div className="space-y-2.5">
                      {(f.routes || []).map((r, idx) => (
                        <div key={r.id || idx} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`pass-route-row-${idx}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-[10.5px] uppercase tracking-wider text-[#1E3A8A] font-extrabold">Route #{idx + 1}</span>
                            {(f.routes || []).length > 1 && (
                              <button type="button" onClick={() => removeRoute(idx)}
                                data-testid={`pass-route-remove-${idx}`}
                                className="w-6 h-6 rounded-md bg-rose-50 text-rose-600 inline-flex items-center justify-center">
                                <Trash size={11} weight="bold"/>
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <RouteStopBtn label="From *" value={r.from_stop} tid={`pass-route-from-${idx}`}
                              onClick={() => setPickerFor({ idx, target: "from_stop", placeholder: "From bus stop" })}/>
                            <RouteStopBtn label="To *" value={r.to_stop} tid={`pass-route-to-${idx}`}
                              onClick={() => setPickerFor({ idx, target: "to_stop", placeholder: "To bus stop" })}/>
                          </div>
                          <RouteStopBtn label="Via" value={r.via_stop} tid={`pass-route-via-${idx}`}
                            optional
                            onClick={() => setPickerFor({ idx, target: "via_stop", placeholder: "Via bus stop (optional)" })}/>
                          <div className="grid grid-cols-3 gap-2 text-[11.5px]">
                            <Metric label="Distance" value={r.distance_km ? `${r.distance_km} km` : "—"}/>
                            <Metric label="Single Fare" value={r.single_fare ? `₹${r.single_fare}` : "—"}/>
                            <Metric label="Amount" value={r.amount ? `₹${r.amount}` : "—"} highlight tid={`pass-route-amount-${idx}`}/>
                          </div>
                          {r.fare_source === "distance" && (
                            <div className="text-[10.5px] text-amber-700">⚠ Distance-based estimate (no official GTFS fare for this pair).</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Section>

                  {requireExtended && (
                    <Section title="Registration Consent *">
                      <label className="flex gap-3 p-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" data-testid="pass-consent-check" checked={f.consent_signed} onChange={e=>setF({...f, consent_signed:e.target.checked})} className="mt-1"/>
                        <span className="text-xs leading-relaxed text-slate-700">
                          {requireResidency ? (
                            <>
                              I {f.gender === "Female" ? "Mrs / Miss" : f.gender === "Male" ? "Mr" : "Mr / Mrs / Miss"} <b>{[f.first_name, f.last_name].filter(Boolean).join(" ") || "____ ____"}</b> do hereby declare that, I am <b>not</b> an employee of Central or State Government, nor of any Central/State Government Autonomous Body, nor of any State Government Aided Institution / Organisation.<br/><br/>
                              I am giving this declaration to avail Government Concessional Seasonal Pass on Kadamba Transport Corporation Ltd.<br/><br/>
                              I am aware that the above Pass Scheme is not eligible to employees of any State / Central Government Departments / Organisations. I am attaching a self-attested photocopy (Aadhar Card) of my Identity Proof for record.
                            </>
                          ) : (
                            <>
                              I hereby confirm that the data submitted in the above form is accurate and complete to the best of my knowledge. I understand that any false information may lead to the rejection of my application.
                            </>
                          )}
                        </span>
                      </label>
                    </Section>
                  )}
                </>
              )}
            </div>

            <div className="sticky bottom-0 bg-white p-5 border-t border-slate-200 flex justify-between items-center">
              <div className="text-sm">
                <div className="text-slate-500 text-xs">Pass Amount ₹{f.amount} + Registration ₹{opts.registration_fee ?? 35}</div>
                <div><span className="text-slate-500">Total Payable: </span>
                <span className="font-display text-xl font-bold text-[#1E3A8A]" data-testid="pass-total-payable">₹{(+f.amount || 0) + (+(opts.registration_fee ?? 35))}</span></div>
              </div>
              <div className="flex gap-2">
                {step > 1 && <button onClick={() => setStep(step-1)} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Back</button>}
                {step < (requireExtended?4:3) && <button data-testid="pass-step-next" onClick={() => setStep(step+1)} className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold">Next →</button>}
                {step === (requireExtended?4:3) && <button data-testid="pass-issue-btn" onClick={issue} className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold">Issue Pass · ₹{(+f.amount || 0) + (+(opts.registration_fee ?? 35))}</button>}
              </div>
            </div>
          </div>

          {/* Stop picker overlay — for multi-route From/To/Via */}
          {pickerFor && (
            <PassStopPicker target={pickerFor}
              onClose={() => setPickerFor(null)}
              onPick={(name) => {
                updateRoute(pickerFor.idx, { [pickerFor.target]: name });
                setPickerFor(null);
              }}/>
          )}
        </div>
      )}
    </div>
  );
}

const RouteStopBtn = ({label, value, onClick, tid, optional}) => (
  <button type="button" onClick={onClick} data-testid={tid}
    className="w-full text-left bg-white border border-slate-200 rounded-md px-3 py-2 hover:border-[#1E3A8A] transition-colors">
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
          {label} {optional && <span className="opacity-60 normal-case font-normal">(opt.)</span>}
        </div>
        <div className={`text-[12.5px] font-bold ${value ? "text-slate-900" : "text-slate-400"} truncate`}>
          {value || "Select a stop"}
        </div>
      </div>
      <CaretDown size={13} weight="bold" className="text-slate-400 shrink-0 ml-2"/>
    </div>
  </button>
);

const Metric = ({label, value, highlight, tid}) => (
  <div className={`text-center rounded-md py-1.5 border ${highlight ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
    <div className={`text-[9px] uppercase font-bold ${highlight ? "text-amber-800" : "text-slate-500"}`}>{label}</div>
    <div className={`font-extrabold ${highlight ? "text-amber-900" : "text-slate-900"}`} data-testid={tid}>{value}</div>
  </div>
);

function PassStopPicker({ target, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${PUBLIC_API}/stops/search?q=${encodeURIComponent(q)}&limit=60`);
        const data = await res.json();
        setStops(data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  return (
    <div className="fixed inset-0 bg-black/60 z-[1400] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="pass-stop-picker"
      role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-lg overflow-hidden flex flex-col"
        style={{ maxHeight: "85vh" }} onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                {target.target.replace("_stop", "")}
              </div>
              <h2 className="text-base font-extrabold text-slate-900">{target.placeholder}</h2>
            </div>
            <button onClick={onClose} type="button" aria-label="Close"
              className="w-8 h-8 rounded-md bg-slate-100 text-slate-500"><X size={18}/></button>
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input ref={inputRef} data-testid="pass-stop-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Type bus stop name…"
              className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-md text-sm font-medium outline-none focus:border-[#1E3A8A] focus:bg-white"/>
          </div>
        </div>
        <div className="overflow-y-auto p-2 flex-1">
          {loading && <div className="text-center py-8 text-slate-400 text-sm">Searching…</div>}
          {!loading && stops.length === 0 && (
            <div className="text-center text-slate-500 text-sm py-12">No bus stops match &quot;{q}&quot;</div>
          )}
          {!loading && stops.map((s) => (
            <button key={s.id} type="button"
              data-testid={`pass-stop-pick-${s.id}`}
              onClick={() => onPick(s.name)}
              className="w-full text-left px-3 py-2.5 rounded-md hover:bg-slate-50 flex items-center gap-2.5">
              <MapPin size={14} className="text-[#1E3A8A]"/>
              <div className="flex-1">
                <div className="text-[12.5px] font-bold text-slate-900">{s.name}</div>
                {s.code && <div className="text-[10px] text-slate-400">Code · {s.code}</div>}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const Section = ({title, children}) => (
  <div>
    <div className="label-cap text-slate-500 mb-2">{title}</div>
    {children}
  </div>
);
const Field = ({label, children}) => (<div><div className="text-xs font-medium text-slate-600 mb-1">{label}</div>{children}</div>);
const Inp = ({v, on, type="text", placeholder=""}) => (<input type={type} value={v} placeholder={placeholder} onChange={(e)=>on(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>);
const Sel = ({v, on, options, placeholder}) => (
  <select value={v} onChange={e=>on(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);
const Upload = ({label, url, loading, onUpload}) => (
  <label className={`block p-3 border-2 border-dashed rounded-md cursor-pointer transition-colors ${url?"bg-green-50 border-green-300":"border-slate-300 hover:bg-slate-50"}`}>
    <div className="flex items-center gap-2">
      <FileText size={16} className={url?"text-green-600":"text-slate-400"}/>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold">{label}</div>
        <div className="text-[10px] text-slate-500 truncate">{loading?"Uploading…":(url?"✓ Uploaded · click to replace":"No file chosen · click to upload")}</div>
      </div>
    </div>
    <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={e=>onUpload(e.target.files[0])}/>
  </label>
);
